/**
 * `heysegment report` — emit a generalized, origin-tagged event for the session.
 *
 * The single capture point the agent/VTM (and pull/push internally) use to record
 * activity (validated, file.changed, tool.used, prompt.submitted, …). The event's
 * origin + version are derived server-side from the session.
 *
 * @module
 */
import { Console, Effect, Option, Schema as S } from "effect"
import { Command, Flag } from "effect/unstable/cli"
import { SessionEventType } from "@repo/contracts/rpc/procedures/session"
import { withSession, step } from "../session/with-session"
import { UsageError } from "../errors"
import { SessionClient } from "../rpc"
import { apiUrlFlag, dirFlag } from "./flags"

const typeFlag = Flag.string("type").pipe(
  Flag.withDescription(
    "Event type: validated | file.changed | tool.used | prompt.submitted | …",
  ),
)

const dataFlag = Flag.string("data").pipe(
  Flag.withDescription("Optional JSON object payload"),
  Flag.optional,
)

const DataSchema = S.Record(S.String, S.Unknown)
const decodeType = S.decodeUnknownEffect(SessionEventType)
const decodeData = S.decodeUnknownEffect(DataSchema)

export const reportCommand = Command.make(
  "report",
  { dir: dirFlag, apiUrl: apiUrlFlag, type: typeFlag, data: dataFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, () =>
      Effect.gen(function* () {
        const type = yield* decodeType(flags.type).pipe(
          Effect.mapError(
            () => new UsageError({ reason: `Unknown event type: ${flags.type}` }),
          ),
        )

        const rawData = Option.getOrUndefined(flags.data)
        const data =
          rawData === undefined
            ? undefined
            : yield* Effect.try({
                try: () => JSON.parse(rawData),
                catch: () => new UsageError({ reason: "--data must be valid JSON" }),
              }).pipe(
                Effect.flatMap((parsed) =>
                  decodeData(parsed).pipe(
                    Effect.mapError(
                      () => new UsageError({ reason: "--data must be a JSON object" }),
                    ),
                  ),
                ),
              )

        const client = yield* SessionClient
        yield* step("report", client.report({ type, data }))
        yield* Console.log(`✓ reported ${type}`)
      }),
    ),
).pipe(Command.withDescription("Emit an origin-tagged event for the session"))
